<script>
  /**
   * RouteSelectionControls — the routing page header's selection cluster.
   *
   * Off: a single Select toggle. On: the selected count, Select All, an Actions
   * dropdown, and Cancel. The count and Actions hide at zero selected.
   *
   * Enable greys out when every selected route is already enabled, Disable when
   * every one is already disabled. A mixed selection offers both, and the page
   * narrows each action to the routes that actually need it.
   *
   * @example
   * <RouteSelectionControls
   *   bind:selecting
   *   {selection}
   *   rows={routes}
   *   selected={selectedRoutes}
   *   id="signature-routes"
   *   onAction={onBulkAction}
   *   onSelectAll={() => selection.selectAll(routes)}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  /**
   * Whether selection mode is active. Use `bind:selecting`.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * A selection store from `createSelection()`, keyed by route `_id`.
   * @type {{ subscribe: Function, toggle: Function, selectAll: Function, clear: Function, prune: Function }}
   */
  export let selection;

  /**
   * Every route on the page. The count's denominator.
   * @type {Object[]}
   */
  export let rows = [];

  /**
   * The selected routes, resolved to full objects. Drives the Enable/Disable
   * availability, which needs each route's `status`.
   * @type {Object[]}
   */
  export let selected = [];

  /**
   * Called with the chosen action when a dropdown item is clicked.
   * @type {(action: 'enable' | 'disable' | 'delete') => void}
   */
  export let onAction = () => {};

  /**
   * Called when Select All is clicked.
   * @type {() => void}
   */
  export let onSelectAll = () => {};

  /**
   * Prefix for element ids, so both routing pages can coexist in one DOM.
   * @type {string}
   */
  export let id = 'routes';

  let showMenu = false;

  const enable = () => {
    selecting = true;
  };

  const cancel = () => {
    selecting = false;
    selection.clear();
  };

  // Sveltestrap forwards on:click before applying `disabled`, so a greyed item
  // would still fire this without the guard.
  const choose = (action, isDisabled) => {
    if (isDisabled) {
      return;
    }

    showMenu = false;
    onAction(action);
  };

  $: count = $selection.size;
  // `>=` rather than `===` so a stale row list can't strand the button on "Select All".
  $: allSelected = rows.length > 0 && count >= rows.length;
  $: enabledCount = selected.filter(({ status }) => status).length;
  $: noneToEnable = enabledCount === selected.length;
  $: noneToDisable = enabledCount === 0;
</script>

<!-- Not `.selection`: the users toolbar ships a `:global(.selection .btn)` padding
     override, and a shared class would let either chunk restyle the other's cluster. -->
<div class="route-selection d-flex align-items-center gap-2">
  {#if selecting}
    {#if count}
      <small class="text-muted text-nowrap" data-testid="selection-count">
        {$format('label.BULK_ACTION.SELECTED_COUNT', { values: { checked: count, total: rows.length } })}
      </small>
    {/if}

    <Button
      id="{id}-select-all-btn"
      color="link"
      size="sm"
      class="px-1"
      on:click={() => (allSelected ? selection.clear() : onSelectAll())}
    >
      {$format(allSelected ? 'label.DESELECT_ALL' : 'label.SELECT_ALL')}
    </Button>

    <!-- `count` is guarded twice so Select All can sit between the count and Actions. -->
    {#if count}
      <Dropdown isOpen={showMenu} toggle={() => (showMenu = !showMenu)}>
        <DropdownToggle id="{id}-bulk-actions-btn" color="primary" outline caret>
          {$format('label.ACTIONS')}
        </DropdownToggle>
        <DropdownMenu end>
          <DropdownItem disabled={noneToEnable} on:click={() => choose('enable', noneToEnable)}>
            {$format('label.ENABLE')}
          </DropdownItem>
          <DropdownItem disabled={noneToDisable} on:click={() => choose('disable', noneToDisable)}>
            {$format('label.DISABLE')}
          </DropdownItem>
          <DropdownItem divider />
          <DropdownItem class="text-danger" on:click={() => choose('delete', false)}>
            {$format('label.DELETE')}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    {/if}

    <Button on:click={cancel}>
      <Icon class="d-inline d-md-none" name="x" />
      <span class="d-none d-md-inline">{$format('label.CANCEL')}</span>
    </Button>
  {:else}
    <Button id="{id}-select-btn" on:click={enable}>
      <span>{$format('label.SELECT')}</span>
    </Button>
    <Tooltip target="{id}-select-btn" placement="top">{$format('label.SELECT')}</Tooltip>
  {/if}
</div>
